<script setup lang="ts">
import { computed, ref, useSlots, watch } from "vue";
import { useRouter } from "vue-router";

import Button from "~/components/dumb/Button.vue";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import { UpgradeRequiredIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { ButtonStyle, DialogMode, Subscription } from "~/shared/enums";
import { useAppStore, useTenantStore } from "~/stores";
import { useUpgrade } from "~/utils/billing";

// TODO modal remove everything with noButton and just have it use the global one. in fact even have the button ones use the global one
const props = defineProps<{
  featureAction?: string;
  subscription?: Subscription;
  disabled?: boolean;
  isGlobal?: boolean;
  noButton?: boolean;
}>();

const router = useRouter();
const appStore = useAppStore();
const tenantStore = useTenantStore();

const dialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null);
const featureActionNorm = computed(
  () => (props.isGlobal ? appStore.upgradeModalConfig?.featureAction : props.featureAction) ?? "use this feature"
);
const subscriptionNorm = computed(
  () => (props.isGlobal ? appStore.upgradeModalConfig?.subscription : props.subscription) ?? Subscription.PREMIUM
);

const { upgradeCta, getUpgradeDialogTitle, getUpgradeDescription, handleUpgradeClick } = useUpgrade(tenantStore);

const redirectToBilling = () => router.push(makeLinkToSettingsRef("billing").value);

const hasSlot = computed(() => !!useSlots().default || props.noButton);

const onConfirmAction = () => {
  handleUpgradeClick(subscriptionNorm.value);
};

const onSecondaryAction = () => {
  redirectToBilling();
};

const onCancelAction = () => {
  dialog.value?.closeModal();
};

const onAfterLeave = () => {
  if (props.isGlobal) {
    appStore.setUpgradeConfirmationModalOpen(null);
  }
};

watch(
  () => appStore.upgradeModalConfig,
  (open) => {
    if (!open || !props.isGlobal) {
      return;
    }
    dialog.value?.openModal();
  }
);

defineExpose({
  openModal: () => dialog.value?.openModal(),
});
</script>

<template>
  <ConfirmationDialog
    ref="dialog"
    :mode="DialogMode.SECONDARY_ACTION"
    :title="getUpgradeDialogTitle(subscriptionNorm)"
    :description="getUpgradeDescription(featureActionNorm, subscriptionNorm)"
    :confirm-text="upgradeCta"
    cancel-text="See plans"
    :disabled="disabled"
    :main-action-icon="UpgradeRequiredIcon"
    @secondary-action="onSecondaryAction"
    @confirm="onConfirmAction"
    @cancel="onCancelAction"
    @after-leave="onAfterLeave">
    <slot v-if="hasSlot" />
    <Button
      v-else-if="!isGlobal"
      :btn-style="ButtonStyle.SECONDARY"
      :icon="UpgradeRequiredIcon"
      :icon-args="{ class: 'text-vlt' }"
      borderless
      class="!px-1"
      a11y-label="Upgrade" />
  </ConfirmationDialog>
</template>
